/**
 * Payout Method — IN-1.c
 * ──────────────────────
 * Pure writer for `Contractor.payoutMethod`. Configures *where* an
 * entity is paid; NEVER stores ACH/routing numbers. No payment
 * processor integration. Observation-only.
 *
 * Hard rules:
 *   • independent / licensed_contractor / manufacturer_partner → own
 *     payout method required (ach / check / stripe_connect)
 *   • internal_w2 → "payroll"
 *   • branch / crew / helper → "parent_routed" (parentEntityId required,
 *     and must match contractor.parentEntityId when set)
 *   • `last4` is the only sensitive surface allowed
 *
 * Anchor: RECONCILIATION.md §14 (IN-1.c)
 */

import type { Contractor } from "@/data/types";
import { getRequiredCompliance } from "./complianceRequirements";

export type PayoutMethod = NonNullable<Contractor["payoutMethod"]>;
export type PayoutMethodType = PayoutMethod["type"];

export interface SetPayoutMethodInput {
  type: PayoutMethodType;
  verified?: boolean;
  verifiedAt?: string;
  last4?: string;
  parentEntityId?: string;
}

// Disallow common bank-detail keys at the input surface.
const FORBIDDEN_KEYS = [
  "routingNumber",
  "accountNumber",
  "routing_number",
  "account_number",
  "iban",
  "swift",
  "ssn",
];

function requireNonEmpty(value: string | undefined, field: string): string {
  const v = (value ?? "").trim();
  if (!v) throw new Error(`payoutMethod: ${field} is required.`);
  return v;
}

function assertNoBankDetails(input: SetPayoutMethodInput): void {
  const bag = input as unknown as Record<string, unknown>;
  for (const key of FORBIDDEN_KEYS) {
    if (Object.prototype.hasOwnProperty.call(bag, key)) {
      throw new Error(
        `payoutMethod: "${key}" is forbidden — IN-1.c stores no raw bank details.`,
      );
    }
  }
  if (input.last4 && !/^\d{4}$/.test(input.last4)) {
    throw new Error(`payoutMethod: last4 must be exactly 4 digits.`);
  }
}

/**
 * Validate a payout-method shape against the contractor's required
 * `bankingMode` (from `getRequiredCompliance`). Returns a list of
 * blockers (empty array = valid).
 */
export function validatePayoutMethodForEntity(
  contractor: Pick<Contractor, "entityType" | "parentEntityId">,
  method: PayoutMethod | undefined,
  parent?: Pick<Contractor, "id" | "entityType">,
): string[] {
  const reqs = getRequiredCompliance(contractor, parent);
  const blockers: string[] = [];
  if (!reqs.bankingRequired) {
    // Mode is parent_routed or payroll. Method may still be set but
    // must match the required mode.
    if (method) {
      if (reqs.bankingMode === "payroll" && method.type !== "payroll") {
        blockers.push(`payout method type must be "payroll" for internal_w2`);
      }
      if (reqs.bankingMode === "parent_routed" && method.type !== "parent_routed") {
        blockers.push(`payout method type must be "parent_routed" for this entity type`);
      }
      if (method.type === "parent_routed") {
        if (!method.parentEntityId) {
          blockers.push(`parent_routed payout requires parentEntityId`);
        } else if (
          contractor.parentEntityId &&
          method.parentEntityId !== contractor.parentEntityId
        ) {
          blockers.push(`parent_routed payout parentEntityId mismatch`);
        }
      }
    }
    return blockers;
  }
  // Own payout required.
  if (!method) {
    blockers.push(`payout method missing`);
    return blockers;
  }
  if (method.type === "parent_routed" || method.type === "payroll") {
    blockers.push(`entity requires own payout method (ach / check / stripe_connect)`);
  }
  if (!method.verified) {
    blockers.push(`payout method not verified`);
  }
  return blockers;
}

/**
 * Set (or replace) the contractor's payout method. Pure — returns a
 * new Contractor reference. Validates entity-type → bankingMode
 * compatibility and forbids raw bank details.
 */
export function setPayoutMethod(
  contractor: Contractor,
  input: SetPayoutMethodInput,
  actor: string,
  parent?: Pick<Contractor, "id" | "entityType">,
): Contractor {
  if (!contractor) throw new Error("payoutMethod: contractor is required.");
  requireNonEmpty(actor, "actor");
  if (!input?.type) throw new Error("payoutMethod: type is required.");
  assertNoBankDetails(input);

  const reqs = getRequiredCompliance(contractor, parent);

  // Entity-type ↔ type compatibility.
  if (reqs.bankingMode === "payroll" && input.type !== "payroll") {
    throw new Error(
      `payoutMethod: internal_w2 requires type="payroll", got "${input.type}".`,
    );
  }
  if (reqs.bankingMode === "parent_routed" && input.type !== "parent_routed") {
    throw new Error(
      `payoutMethod: ${contractor.entityType} requires type="parent_routed", got "${input.type}".`,
    );
  }
  if (
    reqs.bankingMode === "own" &&
    (input.type === "payroll" || input.type === "parent_routed")
  ) {
    throw new Error(
      `payoutMethod: ${contractor.entityType ?? "independent"} requires own payout (ach/check/stripe_connect), got "${input.type}".`,
    );
  }
  if (input.type === "parent_routed") {
    const pid = (input.parentEntityId ?? "").trim();
    if (!pid) {
      throw new Error(`payoutMethod: parent_routed requires parentEntityId.`);
    }
    if (contractor.parentEntityId && pid !== contractor.parentEntityId) {
      throw new Error(
        `payoutMethod: parentEntityId mismatch with contractor.parentEntityId.`,
      );
    }
  }

  const verified = input.verified ?? false;
  const next: Contractor = {
    ...contractor,
    payoutMethod: {
      type: input.type,
      verified,
      verifiedAt: verified ? input.verifiedAt ?? new Date().toISOString() : undefined,
      last4: input.last4,
      parentEntityId: input.parentEntityId,
    },
  };
  return next;
}
